import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  map,
} from 'rxjs';
import { CapacityHour } from 'src/app/models/capacity.model';
import {
  IPeopleSelect,
  IReservation,
  ReservationAvailabilityReportByDate,
} from 'src/app/models/reservation.model';
import {
  MenuItem,
  MenuItemBook,
  MenuItemBookDetail,
} from 'src/app/models/rich-data.model';
import { INITIAL_RESERVATION } from '../utils/constants/initial-reservation';
import { FormFieldTypes } from '../utils/enums/form-field-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ReservationStateService {
  reservationMaxLimit: number;
  disableNext = false;

  reservationData$: BehaviorSubject<IReservation> =
    new BehaviorSubject<IReservation>(INITIAL_RESERVATION);

  selectedRestaurant$: BehaviorSubject<MenuItem | null> =
    new BehaviorSubject<MenuItem | null>(null);

  selectedProgram$: BehaviorSubject<MenuItemBookDetail | null> =
    new BehaviorSubject<MenuItemBookDetail | null>(null);

  selectedCapacity$: BehaviorSubject<CapacityHour | null> =
    new BehaviorSubject<CapacityHour | null>(null);

  availableReports$: BehaviorSubject<ReservationAvailabilityReportByDate[]> =
    new BehaviorSubject<ReservationAvailabilityReportByDate[]>([]);

  availableDates$: Observable<Date[]> = this.availableReports$.pipe(
    map((reports) => {
      const result: Date[] = [];

      reports.forEach((report) => {
        if (Object.keys(report.reportData).length) {
          const [day, month, year] = report.date.split('.');
          result.push(new Date(+year, +month - 1, +day));
        }
      });

      return result;
    })
  );

  restaurantAvailableDates$: Observable<Date[]> = combineLatest([
    this.selectedRestaurant$,
    this.availableDates$,
    this.availableReports$,
  ]).pipe(
    debounceTime(100),
    map(([restaurant, availableDates, reports]) => {
      const result: Date[] = [];

      if (!restaurant) {
        return result;
      }

      if (!availableDates.length) {
        availableDates = Array.from(
          { length: 15 },
          (_, i) => new Date(Date.now() + i * 24 * 60 * 60 * 1000)
        );
      }

      availableDates.forEach((date) => {
        if (
          this.calculateRestaurantAvailability(restaurant, date, reports).length
        ) {
          result.push(date);
        }
      });

      return result;
    })
  );

  menuItemBookDetails$: Observable<MenuItemBookDetail[]> = combineLatest([
    this.selectedRestaurant$,
    this.reservationData$,
    this.availableReports$,
  ]).pipe(
    map(([restaurant, { date }, reports]) =>
      this.calculateRestaurantAvailability(restaurant, date, reports)
    )
  );

  reservationTotalPrice$ = combineLatest([
    this.reservationData$,
    this.selectedProgram$,
  ]).pipe(
    map(([reservationData, selectedProgram]) =>
      this.calculateTotalPrice(reservationData, selectedProgram)
    )
  );

  reservationOriginalPrice$ = combineLatest([
    this.reservationData$,
    this.selectedProgram$,
  ]).pipe(
    map(([reservationData, selectedProgram]) =>
      this.calculateOriginalTotalPrice(reservationData, selectedProgram)
    )
  );

  constructor() {}

  calculateOriginalTotalPrice(
    reservationData: IReservation,
    selectedProgram: MenuItemBookDetail
  ): number {
    let totalPrice = 0;

    if (!selectedProgram || !this.selectedCapacity$.value) {
      return totalPrice;
    }

    if (selectedProgram.is_deposit) {
      totalPrice += selectedProgram.deposit;
    }

    const { adults, children, infants } = reservationData;

    totalPrice +=
      selectedProgram.price * adults +
      selectedProgram.child_price * children +
      selectedProgram.baby_price * infants;

    return totalPrice;
  }

  calculateTotalPrice(
    reservationData: IReservation,
    selectedProgram: MenuItemBookDetail
  ): number {
    let totalPrice = 0;

    if (!selectedProgram || !this.selectedCapacity$.value) {
      return totalPrice;
    }

    if (selectedProgram.is_deposit) {
      totalPrice += selectedProgram.deposit;
    }

    const { adults, children, infants } = reservationData;

    totalPrice +=
      (selectedProgram.activate_offer
        ? selectedProgram.offer_price
        : selectedProgram.price) *
        adults +
      selectedProgram.child_price * children +
      selectedProgram.baby_price * infants;

    return totalPrice;
  }

  setDate(date: Date) {
    this.reservationData$.next({ ...this.reservationData$.value, date });
    this.selectedCapacity$.next(null);
    this.setTime('');
  }

  setNotes(note: string) {
    this.reservationData$.next({ ...this.reservationData$.value, note });
  }

  setPeople(people: IPeopleSelect) {
    this.reservationData$.next({ ...this.reservationData$.value, ...people });
  }

  setRestaurant(restaurant: MenuItem) {
    this.selectedRestaurant$.next(restaurant);
    this.selectedRestaurant$.value?.form_fields?.forEach((field) => {
      switch (field.field_type) {
        case FormFieldTypes.Text:
          field.value = '';
          break;
        case FormFieldTypes.Number:
          field.value = '0';
          break;
        case FormFieldTypes.Boolean:
          field.value = 'false';
          break;
        // case FormFieldTypes.DatePicker:
        //   field.value = dayjs().format('YYYY-MM-DD');
        //   break;
        // case FormFieldTypes.DateTimePicker:
        //   field.value = dayjs().format('YYYY-MM-DDTHH:mm');
        //   break;
      }
    });
  }

  setProgram(program: MenuItemBookDetail) {
    this.reservationData$.next({
      ...this.reservationData$.value,
      reservationProgramId: program.id,
    });

    this.selectedProgram$.next(program);
  }

  setTime(time: string) {
    this.reservationData$.next({
      ...this.reservationData$.value,
      time,
    });
  }

  resetState() {
    const { date, ...initialReservation } = INITIAL_RESERVATION;

    this.reservationData$.next({
      ...this.reservationData$.value,
      ...initialReservation,
    });
    this.selectedRestaurant$.value?.form_fields?.forEach((field) => {
      switch (field.field_type) {
        case FormFieldTypes.Text:
          field.value = '';
          break;
        case FormFieldTypes.Number:
          field.value = '0';
          break;
        case FormFieldTypes.Boolean:
          field.value = 'false';
          break;
        case FormFieldTypes.DatePicker:
          field.value = dayjs().format('YYYY-MM-DD');
          break;
        case FormFieldTypes.DateTimePicker:
          field.value = dayjs().format('YYYY-MM-DDTHH:mm');
          break;
      }
    });

    this.selectedRestaurant$.next(null);
    this.selectedProgram$.next(null);
  }

  private calculateRestaurantAvailability(
    restaurant: MenuItem,
    date: Date,
    reports: ReservationAvailabilityReportByDate[]
  ): MenuItemBookDetail[] {
    const result: MenuItemBookDetail[] = [];

    if (!reports?.length) {
      for (const menuItemBook of restaurant?.menu_item_books || []) {
        if (!menuItemBook.menu_item_book_details) {
          continue;
        }

        if (
          menuItemBook[
            dayjs(date).format('dddd').toLowerCase() as keyof MenuItemBook
          ]
        ) {
          result.push(...menuItemBook.menu_item_book_details);
        }
      }

      return result;
    }

    restaurant?.menu_item_books?.forEach((book) => {
      if (!book.menu_item_book_details) {
        return;
      }

      const report = reports.find(
        (x) => x.date === dayjs(date).format('DD.MM.YYYY')
      );

      book.menu_item_book_details.forEach((detail) => {
        if (
          report &&
          report.reportData[detail.menu_item_id] &&
          report.reportData[detail.menu_item_id].availableScheduleIds.includes(
            detail.id
          )
        ) {
          result.push(detail);
        }
      });
    });

    return result;
  }
}
